/**
 * Scanner de QR Code usando a câmera do dispositivo.
 * Usado no modo Enviar para ler o QR para receber e preencher o formulário.
 * No mobile: exige toque em "Iniciar câmera" para evitar fechamento ao pedir permissão.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Camera } from 'lucide-react';
import Modal from '@/components/ui/Modal';

// Evita que "Cannot stop, scanner is not running or paused" chegue ao Error Boundary (html5-qrcode lança ao chamar stop() em estado inválido)
function safeStop(scanner) {
  if (!scanner) return;
  try {
    const p = scanner.stop();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch (_) {
    // ignorar: scanner já parado ou em estado inválido
  }
}

export default function QRScanner({ isOpen, onClose, onScan }) {
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [scanStarted, setScanStarted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const scannerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onScanRef = useRef(onScan);

  onCloseRef.current = onClose;
  onScanRef.current = onScan;

  const handleRetry = () => {
    setError(null);
    setHasPermission(null);
    setScanStarted(false);
    setRetryCount((c) => c + 1);
  };

  useEffect(() => {
    if (!isOpen) {
      setRetryCount(0);
      setScanStarted(false);
      setError(null);
      setHasPermission(null);
    }
  }, [isOpen]);

  // Só inicia o scanner quando o usuário tocar em "Iniciar câmera" (evita fechar ao pedir permissão no mobile)
  useEffect(() => {
    if (!isOpen || !scanStarted) return;

    let html5QrCode = null;

    const startScanner = async () => {
      try {
        setError(null);
        setHasPermission(null);

        html5QrCode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            try {
              if (!decodedText || typeof decodedText !== 'string' || !decodedText.trim()) return;
              safeStop(html5QrCode);
              onScanRef.current(decodedText.trim());
              onCloseRef.current();
            } catch (e) {
              console.error('[QRScanner] Erro no callback de sucesso:', e);
            }
          },
          () => {}
        );

        setHasPermission(true);
      } catch (err) {
        console.error('[QRScanner] Erro ao iniciar:', err);
        setHasPermission(false);
        if (err.message?.includes('Permission') || err.message?.includes('denied') || err.message?.includes('NotAllowed')) {
          setError('Permissão de câmera negada. Use as instruções abaixo para permitir.');
        } else if (err.message?.includes('NotFoundError') || err.message?.includes('Requested device not found')) {
          setError('Câmera não encontrada no dispositivo.');
        } else {
          setError('Não foi possível acessar a câmera. Siga as instruções abaixo para resetar.');
        }
      }
    };

    const t = setTimeout(startScanner, 400);

    return () => {
      clearTimeout(t);
      if (scannerRef.current) {
        safeStop(scannerRef.current);
        scannerRef.current = null;
      }
    };
  }, [isOpen, scanStarted, retryCount]);

  const handleClose = () => {
    if (scannerRef.current) {
      safeStop(scannerRef.current);
      scannerRef.current = null;
    }
    onCloseRef.current();
  };

  const handleStartCamera = () => {
    setScanStarted(true);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Escanear QR Code">
      <div className="flex flex-col items-center gap-4">
        <p className="text-gray-400 text-sm text-center">
          Aponte a câmera para o QR Code para receber
        </p>

        <div
          id="qr-reader"
          className="w-full max-w-[300px] aspect-square bg-gray-900 rounded-xl overflow-hidden min-h-[200px]"
        />

        {/* Antes de iniciar: botão para o usuário tocar (estabiliza o modal no mobile) */}
        {!scanStarted && !error && (
          <button
            type="button"
            onClick={handleStartCamera}
            className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition flex items-center justify-center gap-2"
          >
            <Camera size={20} />
            Iniciar câmera
          </button>
        )}

        {scanStarted && hasPermission === null && !error && (
          <div className="flex items-center gap-2 text-gray-400">
            <Camera size={20} className="animate-pulse" />
            <span>Iniciando câmera...</span>
          </div>
        )}

        {error && (
          <>
            <div className="text-red-400 text-sm text-center p-3 bg-red-900/20 rounded-lg w-full">
              {error}
            </div>
            <div className="text-gray-400 text-xs text-center p-3 bg-gray-800/50 rounded-lg w-full space-y-1">
              <p className="font-medium text-gray-300">Para resetar a permissão da câmera:</p>
              <p>1. Feche esta aba e abra o site de novo, ou</p>
              <p>2. No navegador: Menu (⋮) → Configurações → Site → {typeof window !== 'undefined' ? new URL(window.location.origin).host : 'este site'} → Permissões → Câmera: <strong>Permitir</strong>, ou</p>
              <p>3. Limpe os dados do site para este endereço e recarregue a página.</p>
            </div>
            <button
              type="button"
              onClick={handleRetry}
              className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition flex items-center justify-center gap-2"
            >
              <Camera size={18} />
              Tentar novamente
            </button>
          </>
        )}

        <button
          type="button"
          onClick={handleClose}
          className="w-full py-3 px-4 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-xl transition"
        >
          Cancelar
        </button>
      </div>
    </Modal>
  );
}
